import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ToolbarComponent } from '../../../shared/components/toolbar/toolbar.component';
import { ToastService } from '../../../shared/services/toast.service';
import { ImageUploadComponent } from '../../../shared/components/file-upload/file-upload.component';
import { environment } from '../../../environments/environment';
import { IDigitalProductBillingType } from '../../../digitalProduct/models/digitalProductBillingType.model';
import { IDigitalProductRecurringInterval } from '../../../digitalProduct/models/digitalProductRecurringInterval.model';
import { IDigitalProductVisibility } from '../../../digitalProduct/models/digitalProductVisibility.model';
import { IDigitalProduct } from '../../../digitalProduct/models/digitalProduct.model';
import { SubscriptionService } from '../../services/subscription.service';
import { subscriptionActions } from '../../store/subscription.actions';
import { getCurrentSubscriptionSelector } from '../../store/subscription.selectors';

@Component({
    selector: 'clp-details-subscription',
    styleUrls: ['./subscriptionDetails.component.scss'],
    templateUrl: './subscriptionDetails.component.html',
    imports: [
        ToolbarComponent,
        RouterModule,
        TranslateModule,
        CommonModule,
        ReactiveFormsModule,
        ImageUploadComponent
    ],
    providers: [
        SubscriptionService,
        ToastrService
    ]
})
export class SubscriptionDetailsComponent implements OnInit {
    private subscriptionService = inject(SubscriptionService);
    private toastService = inject(ToastService);
    private translate = inject(TranslateService);
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private store = inject(Store);

    subscriptionForm!: FormGroup;
    subscription$!: Observable<IDigitalProduct>;
    id!: string | null;
    edit = false;
    imageUrl: string | undefined;
    currentImageFile?: string;

    billingTypes: { value: IDigitalProductBillingType, label: string }[] = [];
    recurringIntervals: { value: IDigitalProductRecurringInterval, label: string }[] = [];
    visibilities: { value: IDigitalProductVisibility, label: string }[] = [];
    private readonly recurringIntervalMap: Record<string, IDigitalProductRecurringInterval> = {
        '0': IDigitalProductRecurringInterval.None,
        'None': IDigitalProductRecurringInterval.None,
        '1': IDigitalProductRecurringInterval.Day,
        'Day': IDigitalProductRecurringInterval.Day,
        '2': IDigitalProductRecurringInterval.Week,
        'Week': IDigitalProductRecurringInterval.Week,
        '3': IDigitalProductRecurringInterval.Month,
        'Month': IDigitalProductRecurringInterval.Month,
        '4': IDigitalProductRecurringInterval.Year,
        'Year': IDigitalProductRecurringInterval.Year
    };
    private readonly visibilityMap: Record<string, IDigitalProductVisibility> = {
        '1': IDigitalProductVisibility.Draft,
        'Draft': IDigitalProductVisibility.Draft,
        '2': IDigitalProductVisibility.Published,
        'Published': IDigitalProductVisibility.Published,
        '3': IDigitalProductVisibility.Archived,
        'Archived': IDigitalProductVisibility.Archived
    };

    ngOnInit(): void {
        this.id = this.route.snapshot.paramMap.get('id');
        this.edit = this.id !== undefined && this.id !== null && this.id !== '';
        this.imageUrl = undefined;
        this.currentImageFile = undefined;

        this.initializeForm();
        this.initializeSelectOptions();
        this.applyRecurringValidators();
        this.loadSubscriptionData();
    }

    initializeForm(): void {
        this.subscriptionForm = new FormGroup({
            id: new FormControl(''),
            name: new FormControl('', [Validators.required, Validators.maxLength(150)]),
            description: new FormControl('', [Validators.maxLength(4000)]),
            coverUrl: new FormControl('', [Validators.maxLength(500)]),
            currency: new FormControl('USD', [Validators.required, Validators.maxLength(4)]),
            billingType: new FormControl(IDigitalProductBillingType.Recurring, Validators.required),
            recurringInterval: new FormControl(IDigitalProductRecurringInterval.Month),
            recurringIntervalCount: new FormControl(null),
            trialDays: new FormControl(null),
            oneTimeFee: new FormControl(null),
            recurringFee: new FormControl(null),
            visibility: new FormControl(IDigitalProductVisibility.Draft, Validators.required)
        });
    }

    initializeSelectOptions(): void {
        this.billingTypes = Object.entries(IDigitalProductBillingType)
            .filter(([key, value]) => typeof value === 'number')
            .filter(([, value]) => value === IDigitalProductBillingType.Recurring)
            .map(([key, value]) => ({ value: value as IDigitalProductBillingType, label: `subscription.billingType.${key}` }));

        this.recurringIntervals = Object.entries(IDigitalProductRecurringInterval)
            .filter(([key, value]) => typeof value === 'number')
            .filter(([, value]) => value !== IDigitalProductRecurringInterval.None)
            .map(([key, value]) => ({ value: value as IDigitalProductRecurringInterval, label: `subscription.recurringInterval.${key}` }));

        this.visibilities = Object.entries(IDigitalProductVisibility)
            .filter(([key, value]) => typeof value === 'number')
            .map(([key, value]) => ({ value: value as IDigitalProductVisibility, label: `subscription.visibility.${key}` }));
    }

    applyRecurringValidators(): void {
        const recurringInterval = this.subscriptionForm.controls['recurringInterval'];
        const recurringIntervalCount = this.subscriptionForm.controls['recurringIntervalCount'];
        const trialDays = this.subscriptionForm.controls['trialDays'];
        const recurringFee = this.subscriptionForm.controls['recurringFee'];
        const oneTimeFee = this.subscriptionForm.controls['oneTimeFee'];
        const billingType = this.subscriptionForm.controls['billingType'];

        recurringInterval.setValidators([Validators.required]);
        recurringIntervalCount.setValidators([Validators.required, Validators.min(1), Validators.max(365)]);
        trialDays.setValidators([Validators.min(0), Validators.max(365)]);
        recurringFee.setValidators([Validators.required, Validators.min(0.01)]);
        oneTimeFee.clearValidators();

        billingType.setValue(IDigitalProductBillingType.Recurring, { emitEvent: false });
        oneTimeFee.setValue(null, { emitEvent: false });
        if (this.resolveRecurringInterval(recurringInterval.value) === IDigitalProductRecurringInterval.None) {
            recurringInterval.setValue(IDigitalProductRecurringInterval.Month, { emitEvent: false });
        }
        if (recurringIntervalCount.value === null || recurringIntervalCount.value === undefined || recurringIntervalCount.value === '') {
            recurringIntervalCount.setValue(1, { emitEvent: false });
        }

        recurringInterval.updateValueAndValidity({ emitEvent: false });
        recurringIntervalCount.updateValueAndValidity({ emitEvent: false });
        trialDays.updateValueAndValidity({ emitEvent: false });
        recurringFee.updateValueAndValidity({ emitEvent: false });
        oneTimeFee.updateValueAndValidity({ emitEvent: false });
    }

    loadSubscriptionData(): void {
        if (!this.edit || !this.id) {
            return;
        }

        this.store.dispatch(subscriptionActions.getSubscription(this.id));
        this.subscription$ = this.store.select(getCurrentSubscriptionSelector);
        this.subscription$.subscribe(subscription => {
            if (!subscription || !subscription.id || subscription.id !== this.id) {
                return;
            }

            this.subscriptionForm.patchValue({
                id: subscription.id ?? '',
                name: subscription.name ?? '',
                description: subscription.description ?? '',
                coverUrl: subscription.coverUrl ?? '',
                currency: subscription.currency ?? 'USD',
                billingType: IDigitalProductBillingType.Recurring,
                recurringInterval: this.resolveRecurringInterval(subscription.recurringInterval),
                recurringIntervalCount: subscription.recurringIntervalCount ?? null,
                trialDays: subscription.trialDays ?? null,
                oneTimeFee: null,
                recurringFee: subscription.recurringFee ?? null,
                visibility: this.resolveVisibility(subscription.visibility)
            }, { emitEvent: false });

            this.currentImageFile = subscription.coverUrl ?? undefined;
            this.imageUrl = this.resolveCoverImageUrl(this.currentImageFile);
            this.applyRecurringValidators();
        });
    }

    isRecurring(): boolean {
        return true;
    }

    deleteCover(shouldDelete?: boolean): void {
        if (shouldDelete !== true) {
            return;
        }

        this.currentImageFile = undefined;
        this.imageUrl = undefined;
        this.subscriptionForm.controls['coverUrl'].setValue(null, { emitEvent: false });
    }

    coverChanged(file: File | null): void {
        this.subscriptionService.changeCover(file, this.id ?? '').subscribe((image) => {
            this.currentImageFile = image ?? undefined;
            this.imageUrl = this.resolveCoverImageUrl(image);
            this.subscriptionForm.controls['coverUrl'].setValue(image ?? null, { emitEvent: false });
        });
    }

    changeSubscription(formValues: IDigitalProduct): void {
        if (this.subscriptionForm.invalid) {
            this.subscriptionForm.markAllAsTouched();
            return;
        }

        const subscription = this.buildSubscriptionPayload(formValues);
        let request$: Observable<IDigitalProduct>;

        if (this.edit && this.id) {
            subscription.id = this.id;
            request$ = this.subscriptionService.editSubscription(subscription);
        } else {
            request$ = this.subscriptionService.createSubscription(subscription);
        }

        request$.subscribe(savedSubscription => {
            if (savedSubscription?.id) {
                const message = this.edit ? 'subscription.update.successMessage' : 'subscription.create.successMessage';
                this.toastService.show(this.translate.instant(message), '');
                this.store.dispatch(subscriptionActions.getSubscriptions());
                this.router.navigate(['listSubscriptions']);
            }
        });
    }

    private buildSubscriptionPayload(formValues: IDigitalProduct): IDigitalProduct {
        return {
            ...formValues,
            name: (formValues.name ?? '').trim(),
            description: formValues.description?.trim() ?? null,
            coverUrl: this.currentImageFile?.trim() ?? null,
            currency: (formValues.currency ?? 'USD').trim().toUpperCase(),
            billingType: IDigitalProductBillingType.Recurring,
            recurringInterval: this.resolveRecurringInterval(formValues.recurringInterval),
            recurringIntervalCount: this.toNullableNumber(formValues.recurringIntervalCount) ?? 1,
            trialDays: this.toNullableNumber(formValues.trialDays),
            oneTimeFee: null,
            recurringFee: this.toNullableNumber(formValues.recurringFee),
            visibility: this.resolveVisibility(formValues.visibility)
        };
    }

    private resolveRecurringInterval(value: unknown): IDigitalProductRecurringInterval {
        return this.resolveEnumValue(value, this.recurringIntervalMap) ?? IDigitalProductRecurringInterval.Month;
    }

    private resolveVisibility(value: unknown): IDigitalProductVisibility {
        return this.resolveEnumValue(value, this.visibilityMap) ?? IDigitalProductVisibility.Draft;
    }

    private resolveEnumValue<T extends number>(value: unknown, map: Record<string, T>): T | undefined {
        if (value === null || value === undefined) {
            return undefined;
        }

        return map[String(value).trim()];
    }

    private toNullableNumber(value: unknown): number | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        const parsedValue = Number(value);
        return Number.isNaN(parsedValue) ? null : parsedValue;
    }

    private resolveCoverImageUrl(coverUrl?: string | null): string | undefined {
        const normalizedCoverUrl = coverUrl?.trim();
        if (!normalizedCoverUrl) {
            return undefined;
        }

        if (normalizedCoverUrl.startsWith('http://') ||
            normalizedCoverUrl.startsWith('https://') ||
            normalizedCoverUrl.startsWith('data:') ||
            normalizedCoverUrl.startsWith('blob:')) {
            return normalizedCoverUrl;
        }

        const sanitizedCoverUrl = normalizedCoverUrl.replace(/\\/g, '/');

        if (sanitizedCoverUrl.startsWith('/products/images/')) {
            return `${environment.API_URL}${sanitizedCoverUrl}`;
        }

        // Backward compatibility for old values saved as /posts/images/{file}
        const fileName = sanitizedCoverUrl.split('/').filter(Boolean).pop();
        if (!fileName) {
            return undefined;
        }

        return `${environment.API_URL}/products/images/${fileName}`;
    }
}
